import React, { useEffect, useState } from 'react';
import { misc } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, CheckCircle2, XCircle, Award, Stamp, Flame } from 'lucide-react';

const errMsg = (e) => e?.response?.data?.detail || 'Something went wrong. Try again.';

export const NewsQuizModal = ({ article, onClose, onDone }) => {
  const [quiz, setQuiz] = useState(null);
  const [choice, setChoice] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!article) return;
    setQuiz(null); setChoice(null); setResult(null); setError('');
    misc.newsQuiz(article.id).then(setQuiz).catch(e => setError(errMsg(e)));
  }, [article]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await misc.newsAnswer(article.id, choice);
      setResult(r);
      onDone?.(article.id, r);
      if (r.streakBonus) toast({ title: `7-day streak! +${r.streakBonus} bonus pts`, description: 'You read and answered 7 days in a row.' });
      if (r.points) toast({ title: `+${r.points} PBB points!`, description: r.stamp ? 'Plus 1 Engagement stamp on your loyalty card.' : `${r.earnedToday}/${r.cap} reads rewarded today.` });
    } catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };

  const body = () => {
    if (error) return <div className="text-sm text-red-400" data-testid="news-quiz-error">{error}</div>;
    if (!quiz) return <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="news-quiz-loading"><Loader2 size={14} className="animate-spin"/> Preparing your question…</div>;
    if (quiz.status === 'capped') return <div className="text-sm text-gray-300" data-testid="news-quiz-capped">You've earned points for {quiz.cap} articles today. Come back tomorrow for more!</div>;
    if (quiz.status === 'answered') return <div className="text-sm text-gray-300" data-testid="news-quiz-answered">You already answered this one{quiz.points ? ` and earned +${quiz.points} pts` : ''}.</div>;
    return (
      <div className="space-y-3">
        <div className="font-medium text-gray-100" data-testid="news-quiz-question">{quiz.question}</div>
        <div className="space-y-2">
          {quiz.options.map((o, i) => {
            const isAns = result && i === result.answerIndex;
            const isWrongPick = result && i === choice && !result.correct;
            return (
              <button key={i} type="button" disabled={!!result} onClick={() => setChoice(i)} data-testid={`news-quiz-option-${i}`}
                className={`w-full text-left text-sm px-3 py-2.5 rounded-lg border transition-colors ${
                  isAns ? 'border-green-500 bg-green-500/10 text-green-300' :
                  isWrongPick ? 'border-red-500 bg-red-500/10 text-red-300' :
                  choice === i ? 'border-green-500 bg-green-500/5 text-white' : 'border-[#2a2a2a] text-gray-300 hover:border-green-600/50'}`}>
                {o}
              </button>
            );
          })}
        </div>
        {result ? (
          <div className={`flex items-center gap-2 text-sm ${result.correct ? 'text-green-400' : 'text-red-400'}`} data-testid="news-quiz-result">
            {result.correct ? <CheckCircle2 size={16}/> : <XCircle size={16}/>}
            {result.correct
              ? (result.points ? <>Correct! +{result.points} pts{result.stamp && <span className="inline-flex items-center gap-1 text-yellow-400 ml-1"><Stamp size={13}/> +1 stamp</span>}</> : 'Correct! Daily point limit already reached.')
              : 'Not quite. No points for this article, but try another one.'}
          </div>
        ) : null}
        {result ? (
          <div className="flex items-center gap-2 text-xs text-orange-300" data-testid="news-quiz-streak">
            <Flame size={14}/> {result.streakBonus ? `7-day streak complete! +${result.streakBonus} bonus pts` : `Reading streak: ${result.streakDays}/7 days`}
          </div>
        ) : (
          <button type="button" disabled={choice === null || busy} onClick={submit} data-testid="news-quiz-submit"
            className="w-full px-4 py-2.5 rounded-full bg-green-600 hover:bg-green-500 disabled:opacity-40 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin"/>} Submit answer
          </button>
        )}
        <p className="text-[11px] text-gray-500">One try per article.</p>
      </div>
    );
  };

  return (
    <Dialog open={!!article} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0b0b0b] border-[#1f1f1f] text-white max-w-md" data-testid="news-quiz-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Award className="text-green-400" size={18}/> Read & earn {quiz?.pointsPerRead || 10} pts</DialogTitle>
          <DialogDescription className="text-gray-400 line-clamp-2">{article?.title}</DialogDescription>
        </DialogHeader>
        {body()}
      </DialogContent>
    </Dialog>
  );
};
